import { Link } from 'react-router-dom';
import { MdVerified, MdStar, MdLock, MdCheckCircle } from 'react-icons/md';
import AnimatedBackground from './AnimatedBackground';
import SubtleGridBackground from './SubtleGridBackground';
import Footer from './Footer';
import logoImage from '../assets/babysit_logo.png';

export default function AuthLayout({ children, title, subtitle }) {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-50 via-white to-primary-100 dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
      <AnimatedBackground />
      <SubtleGridBackground opacityClass="opacity-[0.015] dark:opacity-[0.02]" />
      
      {/* Content - Centered */}
      <div className="flex-1 relative z-10 flex flex-col">
        <div className="flex-1 flex items-center">
          <div className="max-w-6xl mx-auto px-6 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              {/* Left Panel - Brand (5 columns) - Desktop only */}
              <div className="hidden lg:block lg:col-span-5 relative">
                <div className="space-y-8">
                  {/* Logo + Headline */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <img 
                        src={logoImage} 
                        alt="Nannity Logo" 
                        className="h-12 w-auto"
                      />
                      <span className="text-3xl font-bold gradient-bg bg-clip-text text-transparent">
                        Nannity
                      </span>
                    </div>
                    <h1 className="text-3xl lg:text-4xl font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                      Mreža poverenja sa srcem
                    </h1>
                    <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                      Povezujemo roditelje i dadilje na jednom mestu. Sigurno, brzo, pouzdano.
                    </p>
                  </div>

                  {/* Benefits List */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <MdVerified className="text-primary-600 dark:text-primary-400 text-xl" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                          Provereni profili
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Svi korisnici su verifikovani i provereni
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <MdStar className="text-primary-600 dark:text-primary-400 text-xl" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                          Ocene i recenzije
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Transparentne ocene od strane drugih korisnika
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <MdLock className="text-primary-600 dark:text-primary-400 text-xl" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                          Sigurno plaćanje
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Zaštićene transakcije i automatsko praćenje
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* How It Works Mini */}
                  <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 uppercase tracking-wide">
                      Kako funkcioniše
                    </h3>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 text-center">
                        <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-bold mx-auto mb-2">
                          1
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Pretraži</p>
                      </div>
                      <div className="w-8 h-px bg-gray-300 dark:bg-gray-600"></div>
                      <div className="flex-1 text-center">
                        <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-bold mx-auto mb-2">
                          2
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Kontaktiraj</p>
                      </div>
                      <div className="w-8 h-px bg-gray-300 dark:bg-gray-600"></div>
                      <div className="flex-1 text-center">
                        <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-bold mx-auto mb-2">
                          3
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Započni</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Panel - Form (7 columns) */}
              <div className="lg:col-span-7 flex flex-col items-center justify-center w-full">
                <div className="w-full max-w-md lg:max-w-xl">
                  {/* Mobile Brand Header - Only on small screens */}
                  <div className="md:hidden flex flex-col items-center gap-2 pt-8 pb-4">
                    <div className="flex items-center gap-2">
                      <img 
                        src={logoImage} 
                        alt="Nannity Logo" 
                        className="h-10 w-10"
                      />
                      <span className="text-lg font-semibold text-pink-600 dark:text-pink-400">
                        Nannity
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Mreža poverenja sa srcem
                    </p>
                  </div>

                  {/* "Nazad na početnu" link - Left aligned on all screens */}
                  <div className="mb-4 flex justify-start">
                    <Link 
                      to="/" 
                      className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      <span>←</span>
                      <span>Nazad na početnu</span>
                    </Link>
                  </div>
                  
                  {/* Form content */}
                  {children}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer variant="auth" />
    </div>
  );
}
